import { useQuery } from '@tanstack/react-query';
import { useLocale } from 'next-intl';
import { apiClient } from '@/shared/lib/api';
import { cacheKeys } from '@/shared/lib/cache-invalidation';

/**
 * Server-driven benefits/intro payload for the Estimate tab.
 *
 * All marketing copy (title, subtitle, bullets) is owned by the API
 * (`estimate.intro.*` lang files) — the client renders it verbatim and never
 * composes its own copy (API-driven messages rule). The geo fields drive the
 * region/measurement context line.
 */
export interface EstimateIntroAttributes {
  country_code: string | null;
  region_label: string | null;
  /** Geo-resolved local currency (3-letter ISO) — null when unresolvable. */
  local_currency: string | null;
  measurement_system: 'metric' | 'imperial';
  title: string;
  subtitle: string;
  bullets: string[];
}

interface EstimateIntroResponse {
  data: {
    type: string;
    attributes: EstimateIntroAttributes;
  };
}

export function useEstimateIntro(projectId: string, conceptId: string) {
  const locale = useLocale();
  return useQuery({
    // Locale-keyed: the copy is localized server-side, so a locale switch must
    // refetch the localized intro payload. Invalidated by project prefix on
    // project PATCH (goals/region edits) — see cacheInvalidation.
    queryKey: cacheKeys.estimates.intro(projectId, conceptId, locale),
    queryFn: async () => {
      const response = await apiClient.get<EstimateIntroResponse>(
        `/v1/projects/${projectId}/concepts/${conceptId}/estimates/intro`
      );
      return response.data.attributes;
    },
    staleTime: 60 * 60 * 1000, // 1 hour — copy is static per locale/region
  });
}
